<script setup>
import {join} from 'lodash-es';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';

import TariffsTableDisplayCell from '@/components/layouts/TariffsTableDisplayCell.ce.vue';
import TariffsTableDiffCell from '@/components/layouts/TariffsTableDiffCell.ce.vue';
import TariffsTableInputCell from '@/components/layouts/TariffsTableInputCell.ce.vue';
import TranslationValue from '@/components/layouts/TranslationValue.ce.vue';
import TooltipComponent from '@/components/layouts/TooltipComponent.ce.vue';

import {useChooserFormStore} from '@/store/forms/chooser';
import {useTranslationsStore} from '@/store/translations';

const props = defineProps({
    sectionIndex: {
        type: Number,
        default: 0,
    },
    rowIndex: {
        type: Number,
        default: 0,
    },
    cellHeading: {
        type: String,
        default: '',
    },
    cells: {
        type: Array,
        default: function () {
            return [];
        },
    },
    translationContext: {
        type: Object,
        default: () => {
        },
    },
    isEnabled: {
        type: Boolean,
        default: true,
    },
    isVisible: {
        type: Boolean,
        default: true,
    },
    isHighlighted: {
        type: Boolean,
        default: false,
    },
    hasTotalAmount: {
        type: Boolean,
        default: false,
    },
});

const translationStore = useTranslationsStore();
const chooserFormStore = useChooserFormStore();

const {debug} = storeToRefs(chooserFormStore);
const {trans} = storeToRefs(translationStore);

function getHeadingCss() {
    let classes = [];
    classes.push('px-6 py-4');

    if (isHeading.value) {
        classes.push('font-bold text-slate-50 bg-wageindicator-dark-blue');
    } else {
        classes.push('font-medium  min-w-[200px]');

        if (props.isHighlighted) {
            classes.push(
                'text-white bg-wageindicator-orange-300 cell__is-highlighted'
            );
        } else if (props.hasTotalAmount) {
            classes.push(
                'text-slate-900 font-bold bg-blue-100 cell__has-total-amount'
            );
        } else {
            classes.push('text-gray-900 bg-gray-50');
        }
    }

    return join(classes, ' ');
}

function getCellCss(index, cellLength, isInput) {
    const isLast = index === cellLength - 1;

    let classes = [];
    if (props.isHighlighted) {
        classes.push('text-slate-900 bg-orange-100');
        if (isLast) {
            classes.push('border-l border-orange-300');
        }
    } else if (props.hasTotalAmount) {
        classes.push('text-slate-900 bg-blue-100 cell__has-total-amount');

        if (isLast) {
            classes.push('border-l border-blue-300');
        }
    } else {
        if (isInput) {
            classes.push('bg-blue-200 text-slate-900');
        } else {
            classes.push('text-slate-600');
        }
        if (isLast) {
            classes.push('border-l border-gray-200');
        }
    }

    return join(classes, ' ');
}

const isHeading = computed(() => {
    return props.cells.length === 0;
});

const cssClass = computed(() => {
    return `section-${props.sectionIndex}__row-${props.rowIndex}`;
});

const isDisplayCell = (cell) => {
    return cell && cell.type === undefined;
};
const isInputCell = (cell) => {
    return cell && cell.type === 'input';
};
const isDiffCell = (cell) => {
    return cell && cell.type === 'difference';
};
</script>

<template>
    <tr
        v-if="props.isEnabled"
        v-show="props.isVisible"
        class="bg-white border-b"
        :class="cssClass"
    >
        <th
            :colspan="!isHeading ? null : 3"
            scope="row"
            :class="getHeadingCss()"
            :data-translation-id="props.cellHeading"
        >
            <div class="flex">
                <slot name="heading_pre"/>

                <TranslationValue
                    :id="props.cellHeading"
                    :context="translationContext"
                />

                <div
                    v-if="trans(`${props.cellHeading}_explain`) !== `${props.cellHeading}_explain`"
                    class="grow flex justify-end"
                >
                    <TooltipComponent
                        :id="props.cellHeading"
                        :context="translationContext"
                    />
                </div>
            </div>
            <div
                v-if="debug"
                class="my-2 bg-indigo-100 text-indigo-900 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
            >
                {{ props.cellHeading }}
            </div>
        </th>
        <template v-if="!isHeading">
            <template v-for="(cell, index) in props.cells" :key="index">
                <td
                    class="px-6 py-4 text-right align-bottom"
                    :class="
                        getCellCss(
                            index,
                            props.cells.length,
                            isInputCell(cell)
                        )
                    "
                >
                    <TariffsTableDisplayCell
                        v-if="isDisplayCell(cell)"
                        :cell="cell"
                    />
                    <TariffsTableInputCell
                        v-else-if="isInputCell(cell)"
                        :cell="cell"
                    />
                    <TariffsTableDiffCell
                        v-else-if="isDiffCell(cell)"
                        :cell="cell"
                    />
                    <span v-else>—</span>
                </td>
            </template>
        </template>
    </tr>
</template>
